// Schema builder for the public upload form (BACKLOG.md #6). Operator
// declares which fields the form has, what types they are, what's
// prefilled vs locked. The /form/{slug} route renders HTML from this
// schema and submissions become events.
//
// Extracted from pages/SourceDetailPage.tsx as part of the file split
// (Apr 2026).

import { useState } from 'react';
import type { FormConfig, FormField, Source } from '@datahub/shared';
import { patchFormConfig } from '../../api';
import { useToast } from '../Toast';
import { Inp, Label, inpStyle } from '../common/atoms';

export function FormConfigDialog({ source, onClose, onSaved }: {
  source: Source;
  onClose: () => void;
  onSaved: (config: FormConfig | null) => void;
}) {
  const toast = useToast();
  const [cfg, setCfg] = useState<FormConfig>(source.form_config ?? {
    title: source.name || 'Submit',
    description: 'Drop your file or fill the form below.',
    thank_you_message: 'Thanks · we got it.',
    fields: [
      { id: 'email', label: 'Email', type: 'email', prefill: 'editable', required: true },
      { id: 'attachment', label: 'File', type: 'file', prefill: 'editable', required: false },
    ],
    max_file_size_mb: 25,
    enabled: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const slug = source.slug_alias ?? source.source_id;
  const formUrl = `${window.location.origin}/f/${slug}`;

  function patchField(idx: number, patch: Partial<FormField>): void {
    setCfg(c => ({ ...c, fields: c.fields.map((f, i) => i === idx ? { ...f, ...patch } : f) }));
  }
  function removeField(idx: number): void {
    setCfg(c => ({ ...c, fields: c.fields.filter((_, i) => i !== idx) }));
  }
  function addField(): void {
    const id = `field_${cfg.fields.length + 1}`;
    setCfg(c => ({ ...c, fields: [...c.fields, { id, label: 'New field', type: 'text', prefill: 'editable' }] }));
  }

  async function save(): Promise<void> {
    setSubmitting(true); setError(null);
    try {
      const r = await patchFormConfig(source.source_id, cfg);
      onSaved(r.form_config);
      toast.show(`Form saved · serving at /form/${slug}`, { kind: 'success' });
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  }

  async function disableForm(): Promise<void> {
    const ok = await toast.confirm({
      title: 'Disable public form?',
      message: 'The /form/{slug} URL will return 404. Existing event history is unaffected. Re-enable any time.',
      destructive: true,
      confirmLabel: 'Disable',
    });
    if (!ok) return;
    try {
      const r = await patchFormConfig(source.source_id, null);
      onSaved(r.form_config);
      toast.show('Public form disabled.', { kind: 'success' });
    } catch (e) {
      toast.show(`Failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'oklch(0.18 0.01 250 / 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 60 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(720px, calc(100vw - 48px))', background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 12, padding: 24, maxHeight: 'calc(100vh - 100px)', overflow: 'auto' }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Public upload form</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 14, lineHeight: 1.5 }}>
          Hand a partner / vendor / customer this URL — they fill the form, the
          submission lands as an event on this Source. File attachments go through
          the same GCS raw-uploads pipeline as <code>POST /upload</code>.
        </div>
        <div style={{ marginBottom: 4, padding: '8px 12px', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1 }}>{formUrl}</span>
          <button className="btn sm" onClick={() => { void navigator.clipboard.writeText(formUrl); toast.show('URL copied.', { kind: 'success' }); }}>copy</button>
          <a
            className="btn sm"
            href={source.form_config?.enabled ? formUrl : undefined}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'none', opacity: source.form_config?.enabled ? 1 : 0.4, pointerEvents: source.form_config?.enabled ? 'auto' : 'none' }}
            title={source.form_config?.enabled ? 'Open form in new tab' : 'Save & enable first to make this URL live'}
          >open ↗</a>
        </div>
        <div style={{ marginBottom: 12, fontSize: 11, color: source.form_config?.enabled ? 'var(--accent)' : 'var(--ink-3)' }}>
          {source.form_config?.enabled
            ? '✓ Form is live — partners visiting the URL get the rendered form right now.'
            : 'Form is NOT live yet. Click "Save & enable" below; the URL starts serving the form at that moment.'}
        </div>

        <Label>Title</Label>
        <Inp value={cfg.title ?? ''} onChange={v => setCfg(c => ({ ...c, title: v }))} placeholder="Submit your invoice" />
        <Label>Description <span style={{ textTransform: 'none', color: 'var(--ink-3)' }}>· optional</span></Label>
        <Inp value={cfg.description ?? ''} onChange={v => setCfg(c => ({ ...c, description: v }))} placeholder="One-liner shown above the form" />
        <Label>Thank-you message <span style={{ textTransform: 'none', color: 'var(--ink-3)' }}>· shown after submit</span></Label>
        <Inp value={cfg.thank_you_message ?? ''} onChange={v => setCfg(c => ({ ...c, thank_you_message: v }))} placeholder="Thanks · we got it." />
        <Label>Max file size (MB)</Label>
        <input type="number" min={1} max={100} value={cfg.max_file_size_mb} onChange={e => setCfg(c => ({ ...c, max_file_size_mb: Number(e.target.value) || 25 }))} style={inpStyle} />

        <Label>Fields</Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {cfg.fields.map((f, idx) => (
            <div key={idx} style={{ padding: 10, background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 6 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                <input
                  value={f.id}
                  onChange={e => patchField(idx, { id: e.target.value })}
                  placeholder="id"
                  style={{ width: 140, padding: '4px 8px', fontFamily: 'var(--mono)', fontSize: 12, border: '1px solid var(--line)', borderRadius: 4 }}
                />
                <input
                  value={f.label}
                  onChange={e => patchField(idx, { label: e.target.value })}
                  placeholder="Label"
                  style={{ flex: 1, padding: '4px 8px', fontSize: 12, border: '1px solid var(--line)', borderRadius: 4 }}
                />
                <select
                  value={f.type}
                  onChange={e => patchField(idx, { type: e.target.value as FormField['type'] })}
                  style={{ padding: '4px 8px', fontFamily: 'var(--mono)', fontSize: 12, border: '1px solid var(--line)', borderRadius: 4 }}
                >
                  <option value="text">text</option>
                  <option value="number">number</option>
                  <option value="email">email</option>
                  <option value="textarea">textarea</option>
                  <option value="select">select</option>
                  <option value="file">file</option>
                  <option value="date">date</option>
                  <option value="datetime">datetime</option>
                  <option value="time">time</option>
                </select>
                <button className="btn sm ghost" style={{ color: 'var(--danger)' }} onClick={() => removeField(idx)}>×</button>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <input type="checkbox" checked={f.required ?? false} onChange={e => patchField(idx, { required: e.target.checked })} />
                  required
                </label>
                <select
                  value={f.prefill}
                  onChange={e => patchField(idx, { prefill: e.target.value as FormField['prefill'] })}
                  style={{ padding: '3px 6px', fontFamily: 'var(--mono)', fontSize: 11, border: '1px solid var(--line)', borderRadius: 4 }}
                >
                  <option value="editable">editable</option>
                  <option value="default">default (editable)</option>
                  <option value="locked">locked (readonly)</option>
                </select>
                {f.prefill !== 'editable' && f.type !== 'file' && (
                  <input
                    value={f.default_value ?? ''}
                    onChange={e => patchField(idx, { default_value: e.target.value })}
                    placeholder={(f.type === 'date' || f.type === 'datetime' || f.type === 'time') ? 'value · or "now" / "today"' : 'default value'}
                    style={{ flex: 1, minWidth: 120, padding: '3px 6px', fontFamily: 'var(--mono)', fontSize: 11, border: '1px solid var(--line)', borderRadius: 4 }}
                  />
                )}
                {f.type === 'select' && (
                  <input
                    value={(f.options ?? []).join(', ')}
                    onChange={e => patchField(idx, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                    placeholder="options (comma-separated)"
                    style={{ flex: 2, minWidth: 180, padding: '3px 6px', fontFamily: 'var(--mono)', fontSize: 11, border: '1px solid var(--line)', borderRadius: 4 }}
                  />
                )}
              </div>
            </div>
          ))}
          <button className="btn sm" onClick={addField}>+ Add field</button>
        </div>

        <div style={{ marginTop: 14, fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.5 }}>
          Tip: append <code style={{ fontFamily: 'var(--mono)' }}>?prefill[id]=value</code> to the form URL to pre-fill an editable
          field for a specific recipient.
        </div>

        {error && <div style={{ marginTop: 12, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 16 }}>
          {source.form_config && (
            <button className="btn ghost" style={{ color: 'var(--danger)' }} onClick={() => { void disableForm(); }}>Disable form</button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn ghost" onClick={onClose} disabled={submitting}>Cancel</button>
            <button className="btn primary" onClick={() => { void save(); }} disabled={submitting}>{submitting ? 'Saving…' : 'Save & enable'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
